import i18n from "i18next";
import { currencyToSymbol } from "./currencyToSymbol";

export default function getPriceForChosenDuration(
  space,
  duration,
  t = i18n.t,
  includeDurationKey = true
) {
  const currencySymbol = currencyToSymbol(space.currency);
  const chosenDuration = duration || space.minimal_duration;

  // Helper function to check if space is seminar or teambuilding
  const isSeminarOrTeambuilding = (usageKey) => {
    return usageKey === "seminar" || usageKey === "teambuilding";
  };

  // For seminar/teambuilding spaces, ONLY use seminar prices, ignore base prices completely
  if (isSeminarOrTeambuilding(space.placeUsage?.key) && Array.isArray(space.seminars)) {
    // Convert duration to seminar type format (remove underscore)
    const seminarType = chosenDuration.replace("_", "");
    
    // Get the minimum price for this SPECIFIC seminar type
    const priceForType = getMinPriceByType(space.seminars, seminarType);
    
    // Only return if we got a valid price > 0
    if (priceForType !== null && priceForType > 0) {
      const suffix = "/p.";
      
      if (includeDurationKey) {
        const durationKey = `space.by${chosenDuration
          .split("_")
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join("")}`;
        return `${priceForType} ${currencySymbol} ${t(durationKey)}${suffix}`;
      } else {
        return `${priceForType} ${currencySymbol}${suffix}`;
      }
    }
    
    // No valid price found for this duration
    return null;
  }

  // For non-seminar/teambuilding spaces, use the original logic with minimal_duration
  let price;
  
  // Select base price for the chosen duration
  switch (chosenDuration) {
    case "hour":
      price = space.price_hour;
      break;
    case "half_day":
      price = space.price_half_day;
      break;
    case "day":
      price = space.price_day;
      break;
    case "week":
      price = space.price_week;
      break;
    case "month":
      price = space.price_month;
      break;
    default:
      price = null;
  }

  // Check if this duration is allowed based on minimal_duration
  const isDurationAllowed = (durationToCheck) => {
    const durationOrder = ["hour", "half_day", "day", "week", "month", "residential"];
    const minDurationIndex = durationOrder.indexOf(space.minimal_duration);
    const currentDurationIndex = durationOrder.indexOf(durationToCheck);
    
    // If minimal_duration is not defined, allow all
    if (minDurationIndex === -1) return true;
    return currentDurationIndex >= minDurationIndex;
  };

  // Only show price if duration is allowed and price is valid
  if (!isDurationAllowed(chosenDuration) || !price || price <= 0) {
    return null;
  }

  // Handle the special case for residential duration
  if (chosenDuration === "residential") {
    return includeDurationKey
      ? `${price} ${currencySymbol} ${t("space.byDay")}/p.`
      : `${price} ${currencySymbol}/p`;
  }

  // Set display suffix
  const suffix = "";

  // Determine output format based on chosen duration and price
  if (chosenDuration && price && price > 0) {
    if (includeDurationKey) {
      const durationKey = `space.by${chosenDuration
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join("")}`;
      return `${price} ${currencySymbol} ${t(durationKey)}${suffix}`;
    } else {
      return `${price} ${currencySymbol}${suffix}`;
    }
  } else {
    return null;
  }
}

/**
 * Get the minimum price for a specific seminar type
 * @param {Array} seminars - Array of seminar objects
 * @param {string} type - Seminar type (halfday, day, residential)
 * @returns {number|null} - Minimum price for the type or null if none found
 */
function getMinPriceByType(seminars, type) {
  let minPrice = null;
  
  seminars.forEach((seminar) => {
    // Only consider enabled seminars that match the requested type
    if (seminar.enable === true && seminar.type === type) {
      // Parse price, handle null/undefined/empty string
      const priceValue = seminar.price !== null && seminar.price !== undefined && seminar.price !== "" 
        ? parseFloat(seminar.price) 
        : null;
      
      // Only include if price is a valid positive number
      if (priceValue !== null && !isNaN(priceValue) && priceValue > 0) {
        if (minPrice === null || priceValue < minPrice) {
          minPrice = priceValue;
        }
      }
    }
  });
  
  return minPrice;
}